import { Vec3, vec3, UNIT_Y } from '../math/vec3'
import { fromAxisAngle, mulQuat, rotate } from '../math/quat'
import { clamp } from '../math/scalar'
import { EngineDef } from '../vessel/part'
import { PartInstance } from '../vessel/vessel'

/** Accelerazione di gravita standard. Definita qui e solo qui. */
export const G0 = 9.80665

/** Interpolazione lineare fra valore nel vuoto e valore al livello del mare. */
const atPressure = (vac: number, sl: number, ratio: number): number =>
  vac - (vac - sl) * clamp(ratio, 0, 1)

export const thrustAtPressure = (def: EngineDef, pressureRatio: number): number =>
  atPressure(def.thrustVac, def.thrustSl, pressureRatio)

export const ispAtPressure = (def: EngineDef, pressureRatio: number): number =>
  atPressure(def.ispVac, def.ispSl, pressureRatio)

/** Portata di propellente, kg/s, da F = Isp · g₀ · ṁ. */
export const massFlow = (thrust: number, isp: number): number =>
  isp <= 0 ? 0 : thrust / (isp * G0)

/** Comandi di gimbal normalizzati in [-1, 1]. */
export interface GimbalCommand {
  readonly pitch: number
  readonly yaw: number
}

export const NO_GIMBAL: GimbalCommand = { pitch: 0, yaw: 0 }

/**
 * Versore della spinta nel sistema del veicolo.
 * La spinta nasce lungo +Y locale, viene inclinata dal gimbal e poi
 * portata nel sistema del veicolo dall'orientamento con cui la parte e montata.
 */
export function thrustDirection(part: PartInstance, def: EngineDef, gimbal: GimbalCommand): Vec3 {
  const p = clamp(gimbal.pitch, -1, 1) * def.gimbalRange
  const y = clamp(gimbal.yaw, -1, 1) * def.gimbalRange
  const tilt = mulQuat(fromAxisAngle(vec3(0, 0, 1), p), fromAxisAngle(vec3(1, 0, 0), y))
  return rotate(mulQuat(part.rotation, tilt), UNIT_Y)
}

/** Equazione di Tsiolkovsky: Δv = Isp · g₀ · ln(m₀/m_f). */
export function deltaV(isp: number, massStart: number, massEnd: number): number {
  if (massEnd <= 0 || massStart <= massEnd) return 0
  return isp * G0 * Math.log(massStart / massEnd)
}
